import { LOCALE_BY_LANGUAGE } from '../../components/format-money';
import type { SupportedLanguage } from '../../i18n';

/**
 * One formatter per language and precision: building an `Intl.DateTimeFormat` costs more than
 * using one, and Home re-renders every 5 s (M7).
 */
const CLOCK_FORMATS = new Map<string, Intl.DateTimeFormat>();

/** Options of {@link formatClockTime}. */
interface ClockTimeOptions {
  /** Adds the seconds (HH:MM:SS), for the freshness of the data shown (M7 §4). */
  readonly seconds?: boolean | undefined;
  /** IANA time zone; `undefined` is the device's, as on screen. Tests fix it. */
  readonly timeZone?: string | undefined;
}

/**
 * Formats an instant on 24 h in the device time zone: UTC on the wire, local time on screen
 * (M5 R10). HH:MM by default, used by Home ("Copy stopped since 08:42", mockup AccueilBloque) and
 * by the E8 timeline ("09:14", mockup 7); HH:MM:SS with `{ seconds: true }` for the Home
 * freshness indicator (M7 §4: « Mis à jour à 14:03:27 »).
 *
 * @example formatClockTime('2026-10-12T07:14:00Z', 'fr') // "09:14" in Paris (summer time)
 */
export function formatClockTime(
  isoInstant: string,
  language: SupportedLanguage,
  { seconds = false, timeZone }: ClockTimeOptions = {},
): string {
  const key = `${language}|${seconds ? 's' : 'm'}|${timeZone ?? ''}`;
  let format = CLOCK_FORMATS.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[language], {
      hour: '2-digit',
      minute: '2-digit',
      ...(seconds ? { second: '2-digit' } : {}),
      hourCycle: 'h23',
      timeZone,
    });
    CLOCK_FORMATS.set(key, format);
  }
  return format.format(new Date(isoInstant));
}

/** `en-CA` formats a date as `YYYY-MM-DD`; one formatter per time zone. */
const DAY_KEYS = new Map<string, Intl.DateTimeFormat>();

/**
 * The local calendar day of an instant, `YYYY-MM-DD`, in `timeZone` (the device's when
 * `undefined`): two instants are "the same day" for the trader when their keys are equal. Used by
 * the E9 day groups (M6 R7) and by {@link formatClockTimeOrDate}.
 *
 * @example localDayKey(Date.parse('2026-10-12T22:30:00Z'), 'Europe/Paris') // "2026-10-13"
 */
export function localDayKey(instant: number, timeZone?: string): string {
  const cacheKey = timeZone ?? '';
  let format = DAY_KEYS.get(cacheKey);
  if (format === undefined) {
    format = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    DAY_KEYS.set(cacheKey, format);
  }
  return format.format(instant);
}

/** One "DD/MM" (or "DD/MM/YYYY") formatter per language, year and time zone. */
const SHORT_DATES = new Map<string, Intl.DateTimeFormat>();

/** Options of {@link formatClockTimeOrDate}. */
interface ClockTimeOrDateOptions {
  /** "Now" in epoch milliseconds: the day the trader reads the screen on. */
  readonly now: number;
  /** IANA time zone; `undefined` is the device's. Tests fix it. */
  readonly timeZone?: string | undefined;
}

/**
 * An instant as local HH:MM when it falls on the same local day as `now`, else with its date in
 * front: "13/10 10:05" (and "13/10/2025 10:05" in another year). A bare time of another day would
 * read as today's (M5 §4 "as of", lost position). Proposal, to be validated (M5 §8 "Time without
 * a date"). DD/MM in both languages: the English locale is `en-GB`.
 *
 * @example formatClockTimeOrDate('2026-10-13T08:05:00Z', 'fr', { now, timeZone: 'Europe/Paris' }) // "13/10 10:05" when now is the 14th
 */
export function formatClockTimeOrDate(
  isoInstant: string,
  language: SupportedLanguage,
  { now, timeZone }: ClockTimeOrDateOptions,
): string {
  const time = formatClockTime(isoInstant, language, { timeZone });
  const instant = Date.parse(isoInstant);
  const day = localDayKey(instant, timeZone);
  const today = localDayKey(now, timeZone);
  if (day === today) return time;
  const withYear = day.slice(0, 4) !== today.slice(0, 4);
  const key = `${language}|${String(withYear)}|${timeZone ?? ''}`;
  let format = SHORT_DATES.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[language], {
      day: '2-digit',
      month: '2-digit',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone,
    });
    SHORT_DATES.set(key, format);
  }
  return `${format.format(instant)} ${time}`;
}
